import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Campo, ConfiguracaoFilialType } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";
import ModalForm from "../modal-form";
import { detectarTipoChavePix, formatarChaveParaExibicao, limparChavePix, validarChavePix } from "../minha-conta/ConfiguracaoTab";

interface ConfigIntegracaoProps {
    idFilial: string;
    show: boolean;
    onHide: () => void;
    nomeFilial?: string;
    integracao: 'asaas' | 'whatsapp' | 'autentique' | 'azulmonitor' | 'pix';
}

const TITULOS: Record<string, string> = {
    asaas: 'Asaas — Pagamentos',
    whatsapp: 'WhatsApp — Mensagens',
    autentique: 'Autentique — Assinatura Digital',
    azulmonitor: 'LocaSmartTrack — Rastreamento',
    pix: 'Pix — Chave de recebimento',
};

const CAMPOS: Record<string, Campo[]> = {
    asaas: [
        { label: 'Chave de API Asaas', nome: 'chaveAPIAsaas', tipo: 'text', required: false, largura: 12, coluna: 1, linha: 2, visible: true,
          maximoCaracteres: 250,
          ajudinha: { texto: 'Chave de produção do Asaas (prefixo "$aact_prod_"). Encontre em Asaas > Minha Conta > Integrações.', autoCloseTime: 5 }
        },
    ],
    whatsapp: [
        { label: 'Mensagem de Cobrança', nome: 'mensagemWhatsAppCobranca', tipo: 'memo', required: false, largura: 12, coluna: 1, linha: 2, visible: true,
          ajudinha: { texto: 'Mensagem enviada ao cobrar o locatário. Use {nome}, {valor}, {vencimento} como variáveis.', autoCloseTime: 5 }
        },
        { label: 'Mensagem de Manutenção', nome: 'mensagemWhatsAppManutencao', tipo: 'memo', required: false, largura: 12, coluna: 1, linha: 3, visible: true,
          ajudinha: { texto: 'Mensagem enviada ao notificar sobre manutenção.', autoCloseTime: 5 }
        },
    ],
    autentique: [
        { label: 'API Key Autentique', nome: 'autentiqueApiKey', tipo: 'text', required: false, largura: 12, coluna: 1, linha: 2, visible: true, maximoCaracteres: 600 },
        { label: 'Email Remetente', nome: 'autentiqueRemetenteEmail', tipo: 'email', required: false, largura: 12, coluna: 1, linha: 3, visible: true,
          ajudinha: { texto: 'Email que aparecerá como remetente dos documentos enviados para assinatura.', autoCloseTime: 4 }
        },
    ],
    azulmonitor: [
        { label: 'Email', nome: 'azulMonitorEmail', tipo: 'email', required: false, largura: 12, coluna: 1, linha: 2, visible: true },
        { label: 'Senha', nome: 'azulMonitorSenha', tipo: 'senha', required: false, largura: 12, coluna: 1, linha: 3, visible: true },
    ],
    pix: [
        { label: '', nome: '_avisoPixHerdado', tipo: 'aviso', required: false, largura: 12, coluna: 1, linha: 1,
          visible: (entidade: any) => !!entidade?._chavePixDaEmpresa && !entidade?.chavePix,
          alertMessage: 'Esta unidade está usando a chave Pix da empresa. Informe uma chave abaixo para que as cobranças desta unidade (Pix copia e cola, mensagem de cobrança e app do locatário) usem a chave dela.' },
        { label: 'Tipo da chave', nome: '_tipoChavePix', tipo: 'select', required: false, largura: 4, coluna: 1, linha: 2, visible: true,
          opcoes: [
              { label: 'CPF', valor: 'cpf' }, { label: 'CNPJ', valor: 'cnpj' }, { label: 'E-mail', valor: 'email' },
              { label: 'Telefone', valor: 'telefone' }, { label: 'Chave aleatória', valor: 'aleatoria' },
          ] },
        { label: 'Chave Pix da unidade', nome: 'chavePix', tipo: 'text', required: false, largura: 8, coluna: 2, linha: 2, visible: true, maximoCaracteres: 77,
          ajudinha: { texto: 'Deixe em branco para voltar a usar a chave da empresa.', autoCloseTime: 5 }
        },
    ],
};

const ConfiguracaoFilialTab: React.FC<ConfigIntegracaoProps> = ({
    idFilial,
    show,
    onHide,
    nomeFilial,
    integracao,
}) => {
    const [carregando, setCarregando] = useState(false);
    const [configAtual, setConfigAtual] = useState<any>(null);

    const carregarConfig = useCallback(async () => {
        if (!idFilial || !show) return;
        setCarregando(true);
        try {
            const resp = await api.get(`/ConfiguracaoFilial/Resolvida/${idFilial}`);
            // A configuração "resolvida" traz os valores herdados da EMPRESA quando a filial não tem os
            // próprios. Segredos herdados NÃO podem voltar no PUT: o ModalForm reenvia a entidade inteira
            // e o backend gravaria a chave da empresa como override da filial — que quebraria quando a
            // empresa rotacionasse a chave. Só mantém no formulário o que é override da própria filial.
            const overrides: string[] = resp.data?.camposOverridePorFilial ?? resp.data?.CamposOverridePorFilial ?? [];
            const ehOverride = (campo: string) => overrides.some((c) => c.toLowerCase() === campo.toLowerCase());
            const dados = { ...resp.data, idFilial };
            if (!ehOverride('ChaveAPIAsaas')) dados.chaveAPIAsaas = '';
            if (!ehOverride('AutentiqueApiKey')) dados.autentiqueApiKey = '';
            // A chave Pix herdada da empresa não volta no PUT (viraria override congelado). Fica só
            // para o aviso da aba Pix; o campo mostra apenas a chave própria da unidade.
            dados._chavePixDaEmpresa = ehOverride('ChavePix') ? '' : (resp.data?.chavePix || '');
            if (!ehOverride('ChavePix')) dados.chavePix = '';
            dados._tipoChavePix = dados.chavePix ? detectarTipoChavePix(dados.chavePix) : 'cpf';
            if (dados.chavePix) dados.chavePix = formatarChaveParaExibicao(dados._tipoChavePix, dados.chavePix);
            setConfigAtual(dados);
        } catch {
            setConfigAtual({ idFilial });
        } finally {
            setCarregando(false);
        }
    }, [idFilial, show]);

    useEffect(() => {
        if (show) carregarConfig();
    }, [show, carregarConfig]);

    const onSave = async (entidade: any) => {
        setCarregando(true);
        try {
            // As chaves não vêm mais do servidor (só "configurada"): campo vazio no salvar significa
            // "manter a chave gravada" (o backend trata null como manter e "" como limpar). Para trocar,
            // basta colar a chave nova; para voltar a herdar da empresa, use a tela da empresa/suporte.
            const payload: any = { ...entidade, idFilial };
            if (!payload.chaveAPIAsaas) payload.chaveAPIAsaas = null;
            if (!payload.autentiqueApiKey) payload.autentiqueApiKey = null;
            const tipoChave = (payload._tipoChavePix?.valor ?? payload._tipoChavePix?.value ?? payload._tipoChavePix) || 'cpf';
            delete payload._chavePixDaEmpresa;
            delete payload._avisoPixHerdado;
            delete payload._tipoChavePix;
            // Chave Pix: na aba Pix, vazio limpa (volta a herdar da empresa); nas outras abas o campo não
            // vai (null = o servidor mantém a gravada). Valida e grava sem formatação, como na empresa.
            if (integracao === 'pix') {
                const digitada = String(payload.chavePix || '').trim();
                if (digitada) {
                    const erro = validarChavePix(tipoChave, digitada);
                    if (erro) { toast.error(erro); setCarregando(false); return; }
                    payload.chavePix = limparChavePix(tipoChave, digitada);
                } else {
                    payload.chavePix = '';
                }
            } else {
                payload.chavePix = null;
            }
            const resp = await api.put('/ConfiguracaoFilial', payload);
            if (resp.data?.avisoWebhook) {
                toast.warning(resp.data.avisoWebhook);
            } else {
                toast.success('Configuração salva com sucesso');
            }
            onHide();
        } catch (error: any) {
            toast.error(error.response?.data?.Mensagem || 'Erro ao salvar');
        } finally {
            setCarregando(false);
        }
    };

    return (
        <ModalForm
            show={show}
            onHide={onHide}
            onCancel={onHide}
            titulo={`${TITULOS[integracao]} — ${nomeFilial || 'Unidade'}`}
            campos={CAMPOS[integracao]}
            modo={TipoFormulario.tpEdicao}
            entidadeSelecionada={configAtual}
            onSave={onSave}
            carregando={carregando}
            tamanho="lg"
            apiConfig={{
                endpoint: '/ConfiguracaoFilial',
                requisicaoBemSucedida: () => onHide(),
                tratarErro: (error: any) => toast.error(error.response?.data?.Mensagem || 'Erro'),
                idTenentCampo: 'idFilial'
            }}
        />
    );
};

export default ConfiguracaoFilialTab;
